import { ExcelButton } from "@/controls/buttons/ExcelButton";
import {
	ErrorModal,
	HelmetDexteel,
	getMomentTz,
	useHasPermission,
} from "@dexteel/mesf-core";
import { pageBgColor } from "@/utils/theme";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import type { GridApi } from "ag-grid-community";
import { useEffect, useState } from "react";
import { useIncentiveRealtimeInvalidation } from "../hooks/realtime/useIncentiveRealtimeInvalidation";
import { useIncentiveReportQuery } from "../hooks/queries/useIncentiveReportQuery";
import { EditShiftOverrideModal } from "./components/EditShiftOverrideModal";
import { IncentiveManagerGrid } from "./components/IncentiveManagerGrid";
import { ManagerFilters } from "./components/ManagerFilters";
import type { IncentiveRow } from "../models/IncentiveRow";

const moment = getMomentTz();

type IncentiveManagerPageProps = {
	assetId: number;
};

export const IncentiveManagerPage = ({
	assetId,
}: IncentiveManagerPageProps) => {
	const [error, setError] = useState("");
	const [endDate, setEndDate] = useState(() => moment().startOf("day"));
	const [startDate, setStartDate] = useState(() =>
		moment().startOf("day").subtract(7, "days"),
	);
	const [crews, setCrews] = useState<string[]>([]);
	const [shift, setShift] = useState<string | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [editRow, setEditRow] = useState<IncentiveRow | null>(null);
	const hasPermissionTo = useHasPermission();
	const canEdit = hasPermissionTo("ui.incentive.edit");

	const isDateRangeValid = !startDate.isAfter(endDate, "day");

	const {
		data,
		isLoading,
		error: queryError,
	} = useIncentiveReportQuery(
		startDate,
		endDate,
		crews,
		shift,
		assetId,
		isDateRangeValid,
	);

	const rows = data?.rows;
	const summary = data?.summary;

	useIncentiveRealtimeInvalidation(assetId);

	useEffect(() => {
		if (queryError) setError(queryError.message);
	}, [queryError]);

	const handleExportToExcel = () => {
		if (gridApi) {
			const appendContent = summary
				? [
						{ cells: [] },
						{
							cells: [
								{
									data: { value: "Totals / Avg Inc%", type: "String" },
									styleId: "boldLeft",
								},
								{ data: { value: "", type: "String" } },
								{ data: { value: "", type: "String" } },
								{
									data: {
										value:
											summary.TotalTarget != null
												? `${summary.TotalTarget.toFixed(0)}`
												: "",
										type: "String",
									},
									styleId: "boldRight",
								},
								{
									data: {
										value:
											summary.TotalFinished != null
												? `${summary.TotalFinished.toFixed(0)}`
												: "",
										type: "String",
									},
									styleId: "boldRight",
								},
								{ data: { value: "", type: "String" } },
								{ data: { value: "", type: "String" } },
								{
									data: {
										value:
											summary.AvgIncPercent != null
												? `${summary.AvgIncPercent.toFixed(1)}%`
												: "",
										type: "String",
									},
									styleId: "boldCenter",
								},
							],
						},
					]
				: [];

			gridApi.exportDataAsExcel({
				fileName: "incentive_report.xlsx",
				appendContent,
				processCellCallback: (params) => {
					const col = params.column.getColId();
					const v = params.value;
					if (col === "IncPercent")
						return v != null ? `${Number(v).toFixed(1)}%` : "—";
					if (
						col === "DisplayedTarget" ||
						col === "DisplayedFinished" ||
						col === "TargetTonsOverride"
					)
						return v != null ? Number(v).toFixed(0) : "—";
					return v;
				},
			} as Parameters<typeof gridApi.exportDataAsExcel>[0]);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	return (
		<>
			<HelmetDexteel title="Incentive Manager" />
			<Box
				sx={{
					backgroundColor: pageBgColor,
					minHeight: "calc(100vh - 64px)",
					p: "10px 20px",
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5 }}>
					Incentive Manager
				</Typography>

				<Paper elevation={2} sx={{ px: 1.5, py: 0.75, mb: "10px" }}>
					<Grid2 container spacing={1} alignItems="center">
						<Grid2 size={{ xs: 12, md: "grow" }}>
							<ManagerFilters
								startDate={startDate}
								endDate={endDate}
								crews={crews}
								shift={shift}
								dateError={
									!isDateRangeValid
										? "End must be after or equal to start"
										: undefined
								}
								onStartDateChange={(date) => date && setStartDate(date)}
								onEndDateChange={(date) => date && setEndDate(date)}
								onCrewsChange={setCrews}
								onShiftChange={setShift}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: "auto" }}>
							<ExcelButton
								handleExportToExcel={handleExportToExcel}
								disabled={!rows || rows.length === 0}
							/>
						</Grid2>
					</Grid2>
				</Paper>

				<IncentiveManagerGrid
					rows={rows ?? []}
					summary={summary}
					isLoading={isLoading}
					canEdit={canEdit}
					onEdit={setEditRow}
					onGridReady={setGridApi}
				/>
			</Box>
			{canEdit && (
				<EditShiftOverrideModal
					row={editRow}
					assetId={assetId}
					onHide={() => setEditRow(null)}
				/>
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
